/**
 * Left-hand clinical document viewer of the fax questionnaire screen.
 *
 * Replaces the legacy react-pdf preview with the oasis {@link
 * OasisPdfViewerPanel} (search, zoom, rotate, page navigation) plus the
 * document index built from `clinical_document.document_page_mapping`, so
 * reviewers can jump straight to a source report while answering questions —
 * the same viewer used on Medical Necessity and Final Documents.
 *
 * The viewable URL comes from {@link useDocumentManagement} (download skipped:
 * this pane only previews) and mapping falls back to Firestore because it is
 * not yet reliable on the Postgres general-order payload.
 * @module procedureQuestionaire/components/questionnaireDocumentPanel
 */
import React from "react";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import OasisPdfViewerPanel from "../../../../components/oasisPdfToolbar/OasisPdfViewerPanel";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { useDocumentPageMapping } from "../../../../hooks/useDocumentPageMapping";
import { useDocumentManagement } from "../../../finalDocuments/hooks";

/** Props for {@link QuestionnaireDocumentPanel}. */
export interface QuestionnaireDocumentPanelProps {
  /** Medical PA order id whose clinical document is previewed. */
  orderId: string;
}

/**
 * Renders the order's clinical document with the oasis PDF viewer.
 *
 * @input props - Order id used to resolve the document and its page mapping
 * @return The PDF viewer, or a loader / empty state while unavailable
 */
const QuestionnaireDocumentPanel: React.FC<QuestionnaireDocumentPanelProps> = ({
  orderId,
}) => {
  const { generalOrderDetails, generalOrderDetailsLoading } =
    useDataAccumulatorForSingleOrder();
  const order = generalOrderDetails?.order ?? null;

  const { pdfUrl, isLoading, hasResolvedDocument, error } =
    useDocumentManagement({
      order,
      isLoadingOrder: generalOrderDetailsLoading ?? false,
      orderId,
      skipDocumentDownload: true,
    });

  const { documentPageMapping: firestoreMapping } =
    useDocumentPageMapping(orderId);
  const mappings =
    order?.clinical_document?.document_page_mapping ?? firestoreMapping;

  if (isLoading || !hasResolvedDocument) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoaderMessage message="Fetching documents..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 text-sm text-red-500">
        {error}
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="flex h-full w-full items-center justify-center p-4 text-sm text-primaryGray-10">
        No clinical document available for this order.
      </div>
    );
  }

  return (
    <OasisPdfViewerPanel
      pdfUrl={pdfUrl}
      documentPageMapping={mappings}
      worker
    />
  );
};

export default QuestionnaireDocumentPanel;
export { QuestionnaireDocumentPanel };
